// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Developer-only debug helper: loads the round + holes + players (including ids) and parses out the
// most important fields for the hamburger-menu "Round information" modal. Read-only; gating is in the
// UI. Uses the same endpoints as useScoreAllHoles and tolerates the various field casings the round
// payloads use.

import { useCallback, useState } from 'react';

type UseRoundInfoParams = {
  apiBaseUrl: string;
  token: string | null | undefined;
  roundId: string | null | undefined;
};

export type RoundInfoHole = {
  id: string | null;
  number: number;
  par: number | null;
  strokeIndex: number | null;
};

export type RoundInfoPlayer = {
  id: string;
  name: string;
  scp: string | null;
};

export type RoundInfoGame = {
  id: string;
  name: string | null;
  weight: number | null;
};

export type RoundInfoGamePointRow = {
  playerId: string;
  playerName: string;
  points: number;
};

export type RoundInfoGamePoints = {
  gameId: string;
  gameName: string;
  rows: RoundInfoGamePointRow[];
};

export type RoundInfoResult = {
  roundId: string;
  name: string | null;
  status: string | null;
  tourId: string | null;
  courseId: string | null;
  courseName: string | null;
  playDate: string | null;
  holesCount: number | null;
  holes: RoundInfoHole[];
  players: RoundInfoPlayer[];
  games: RoundInfoGame[];
  gamePoints: RoundInfoGamePoints[];
  subgameCount: number;
};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const arrayFrom = (value: unknown, ...keys: string[]): unknown[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    for (const key of keys) {
      const nested = (value as Record<string, unknown>)[key];
      if (Array.isArray(nested)) return nested;
    }
  }
  return [];
};

const readString = (record: Record<string, unknown>, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
};

const readNumber = (record: Record<string, unknown>, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const parseHoles = (round: Record<string, unknown>): RoundInfoHole[] =>
  asArray(round.holes ?? round.Holes)
    .map((entry) => {
      const r = asRecord(entry);
      if (!r) return null;
      const number = readNumber(r, 'number', 'Number', 'holeNumber', 'HoleNumber');
      if (number === null) return null;
      return {
        id: readString(r, 'id', 'Id', 'holeId', 'HoleId'),
        number,
        par: readNumber(r, 'par', 'Par'),
        strokeIndex: readNumber(r, 'strokeIndex', 'StrokeIndex', 'index', 'Index'),
      };
    })
    .filter((h): h is RoundInfoHole => h !== null)
    .sort((a, b) => a.number - b.number);

const parsePlayers = (data: unknown): RoundInfoPlayer[] =>
  arrayFrom(data, 'players', 'Players')
    .map((entry) => {
      const r = asRecord(entry);
      if (!r) return null;
      const id = readString(r, 'id', 'Id', 'playerId', 'PlayerId');
      if (!id) return null;
      return {
        id,
        name: readString(r, 'name', 'Name', 'displayName', 'DisplayName') ?? id,
        scp: readString(r, 'scp', 'Scp', 'playingHandicap', 'PlayingHandicap', 'shcp', 'Shcp'),
      };
    })
    .filter((p): p is RoundInfoPlayer => p !== null);

// Resolve the round's game-definition ids + weights into named games using the catalogue.
const parseGames = (round: Record<string, unknown>, catalogue: unknown): RoundInfoGame[] => {
  const nameById = new Map<string, string>();
  asArray(catalogue).forEach((entry) => {
    const r = asRecord(entry);
    if (!r) return;
    const id = readString(r, 'id', 'Id');
    const name = readString(r, 'name', 'Name');
    if (id && name) nameById.set(id, name);
  });

  const weightById = new Map<string, number>();
  asArray(round.gameWeights ?? round.GameWeights).forEach((entry) => {
    const r = asRecord(entry);
    if (!r) return;
    const gid = readString(r, 'gameDefinitionId', 'GameDefinitionId');
    const weight = readNumber(r, 'weightPercent', 'WeightPercent', 'weight', 'Weight');
    if (gid && weight !== null) weightById.set(gid, weight);
  });

  return asArray(round.gameDefinitionIds ?? round.GameDefinitionIds)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .map((id) => ({ id, name: nameById.get(id) ?? null, weight: weightById.get(id) ?? null }));
};

// Per-game player points from the round scoreboard (scores grouped by game, summed per player).
const parseGamePoints = (
  scoreboard: unknown,
  players: RoundInfoPlayer[],
  games: RoundInfoGame[]
): RoundInfoGamePoints[] => {
  const playerNameById = new Map(players.map((p) => [p.id, p.name]));
  const gameNameById = new Map(games.map((g) => [g.id, g.name ?? '']));
  const byGame = new Map<string, { gameId: string; gameName: string; rows: Map<string, number> }>();

  arrayFrom(scoreboard, 'scores', 'Scores').forEach((entry) => {
    const r = asRecord(entry);
    if (!r) return;
    const gameId = readString(r, 'gameId', 'GameId') ?? '';
    const name = readString(r, 'gameName', 'GameName') ?? (gameId ? gameNameById.get(gameId) ?? null : null);
    const key = gameId || name;
    const playerId = readString(r, 'playerId', 'PlayerId');
    if (!key || !playerId) return;
    const points = readNumber(r, 'scorePoints', 'ScorePoints') ?? 0;
    if (!byGame.has(key)) {
      byGame.set(key, { gameId: gameId || key, gameName: name || key, rows: new Map() });
    }
    const game = byGame.get(key)!;
    if ((!game.gameName || game.gameName === game.gameId) && name) game.gameName = name;
    game.rows.set(playerId, (game.rows.get(playerId) ?? 0) + points);
  });

  return Array.from(byGame.values()).map((game) => ({
    gameId: game.gameId,
    gameName: game.gameName,
    rows: Array.from(game.rows.entries())
      .map(([playerId, points]) => ({ playerId, playerName: playerNameById.get(playerId) ?? playerId, points }))
      .sort((a, b) => b.points - a.points || a.playerName.localeCompare(b.playerName)),
  }));
};

export const useRoundInfo = ({ apiBaseUrl, token, roundId }: UseRoundInfoParams) => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRoundInfo = useCallback(async (): Promise<RoundInfoResult> => {
    if (!roundId) throw new Error('No active round.');
    if (!token) throw new Error('Not authenticated.');

    setIsRunning(true);
    setError(null);
    const authHeaders = { Authorization: `Bearer ${token}` };
    try {
      const [roundRes, playersRes, subgamesRes, gameDefsRes, scoreboardRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/game-definitions`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/scoreboard`, { headers: authHeaders }),
      ]);
      if (!roundRes.ok) {
        throw new Error(`Failed to load round (${roundRes.status}).`);
      }

      const roundRaw = asRecord(await roundRes.json()) ?? {};
      const course = asRecord(roundRaw.course ?? roundRaw.Course);
      const gameCatalogue = gameDefsRes.ok ? await gameDefsRes.json() : [];
      const scoreboard = scoreboardRes.ok ? await scoreboardRes.json() : null;

      const players = parsePlayers(playersRes.ok ? await playersRes.json() : null);
      const games = parseGames(roundRaw, gameCatalogue);

      const result: RoundInfoResult = {
        roundId,
        name: readString(roundRaw, 'name', 'Name', 'roundName', 'RoundName'),
        status: readString(roundRaw, 'status', 'Status'),
        tourId: readString(roundRaw, 'tourId', 'TourId'),
        courseId:
          readString(roundRaw, 'courseId', 'CourseId') ?? (course ? readString(course, 'id', 'Id') : null),
        courseName:
          readString(roundRaw, 'courseName', 'CourseName') ?? (course ? readString(course, 'name', 'Name') : null),
        playDate: readString(roundRaw, 'playDate', 'PlayDate', 'date', 'Date'),
        holesCount: readNumber(roundRaw, 'holesCount', 'HolesCount'),
        holes: parseHoles(roundRaw),
        players,
        games,
        gamePoints: parseGamePoints(scoreboard, players, games),
        subgameCount: subgamesRes.ok ? asArray(await subgamesRes.json()).length : 0,
      };
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load round info.';
      setError(message);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setIsRunning(false);
    }
  }, [apiBaseUrl, token, roundId]);

  return { loadRoundInfo, isRunning, error };
};
